import {
    Alert,
    Button,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';
import DocumentInputMasked from '../../../components/DocumentInputMasked';
import PhoneInputMasked from '../../../components/PhoneInputMasked';
import Address from '../../../components/Address';
import { PriceInputMasked } from '../../../components/PriceMask';

const schema = Yup.object().shape({
    document: Yup.string().required('CNPJ é obrigatório'),
    companyName: Yup.string().required('Razão Social é obrigatória'),
    tradeName: Yup.string().required('Nome Fantasia é obrigatório'),
    zipcode: Yup.string().required('CEP é obrigatório'),
    address: Yup.string().required('Endereço é obrigatório'),
    number: Yup.string().required('Número é obrigatório'),
    complement: Yup.string().required('Complemento é obrigatório'),
    neighborhood: Yup.string().required('Bairro é obrigatório'),
    city: Yup.string().required('Cidade é obrigatória'),
    state: Yup.string().required('Estado é obrigatório'),
    contractorName: Yup.string().required('Nome do Contratante é obrigatório'),
    contractorDocument: Yup.string().required('CPF do Contratante é obrigatório'),
    contractorPhone: Yup.string().required('Telefone do Contratante é obrigatório'),
    contractorPosition: Yup.string().required('Cargo do Contratante é obrigatório'),
    contractorEmail: Yup.string().email('E-mail inválido').required('E-mail do Contratante é obrigatório'),
});

export function TermStep({ contract, mutate }) {
    const { propostaId } = useParams();
    const { setErrors, enqueueSnackbar } = useErrors();

    const isReadOnly = Boolean(contract?.integrationId);

    const { values, getFieldProps, setFieldValue, setValues, errors, handleSubmit, isSubmitting, isValid, handleBlur } =
        useFormik({
            initialValues: {
                document: '',
                companyName: '',
                tradeName: '',
                isExempt: false,
                isSimpleOptant: false,
                address: '',
                number: '',
                complement: '',
                neighborhood: '',
                city: '',
                state: '',
                zipcode: '',
                contractorName: '',
                contractorDocument: '',
                contractorNationality: '',
                contractorPhone: '',
                contractorPosition: '',
                contractorEmail: '',
            },
            validationSchema: schema,
            validateOnChange: true,
            validateOnMount: true,
            onSubmit: async (vals, { setSubmitting }) => {
                try {
                    setSubmitting(true);
                    await server.put(`subscription/${propostaId}/term`, vals);
                    await mutate();
                    enqueueSnackbar('Dados gravados.');
                } catch (error) {
                    setErrors(error);
                } finally {
                    setSubmitting(false);
                }
            },
        });

    useEffect(() => {
        if (contract?.contractData?.contract) {
            setValues({ ...contract.contractData.contract }, true);
        }
    }, [contract]);

    const plan = contract?.plan;
    const planName = plan?.name ?? contract?.contractData?.plan?.name ?? '';
    const planValue = Number(contract?.totalValue ?? 0);
    const planValueLabel = planValue === 0 ? 'Gratuito' : null;

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Typography variant="h3" component="h2">
                    Termo de aceite
                </Typography>

                {!isReadOnly && (
                    <>
                        <Divider />
                        <Typography variant="body1">
                            Bem-vindo à Eurobem Benefícios! Preencha os dados abaixo para formalizar sua adesão ao clube
                            de benefícios. Após preencher, o contrato será gerado para assinatura digital.
                        </Typography>

                        <Divider>Dados jurídicos</Divider>

                        <TextField
                            label="CNPJ"
                            {...getFieldProps('document')}
                            fullWidth
                            error={!!errors.document}
                            helperText={errors.document}
                            onBlur={handleBlur}
                            InputProps={{
                                inputComponent: DocumentInputMasked,
                                inputProps: { documentType: 'cnpj' },
                            }}
                        />
                        <TextField
                            label="Razão Social"
                            {...getFieldProps('companyName')}
                            fullWidth
                            error={!!errors.companyName}
                            helperText={errors.companyName}
                        />
                        <TextField
                            label="Nome Fantasia"
                            {...getFieldProps('tradeName')}
                            fullWidth
                            error={!!errors.tradeName}
                            helperText={errors.tradeName}
                        />

                        <Address
                            {...getFieldProps('zipcode')}
                            setFieldValue={setFieldValue}
                            address={values?.address}
                            error={!!errors.zipcode}
                            helperText={errors.zipcode}
                        />
                        <Stack direction="row" columnGap={2}>
                            <TextField
                                fullWidth
                                label="Complemento"
                                {...getFieldProps('complement')}
                                error={!!errors.complement}
                                helperText={errors.complement}
                            />
                            <TextField
                                sx={{ width: 150 }}
                                label="Número"
                                {...getFieldProps('number')}
                                error={!!errors.number}
                                helperText={errors.number}
                            />
                        </Stack>

                        <Divider>Dados do responsável contratante</Divider>

                        <TextField
                            label="CPF do contratante"
                            {...getFieldProps('contractorDocument')}
                            fullWidth
                            error={!!errors.contractorDocument}
                            helperText={errors.contractorDocument}
                            onBlur={handleBlur}
                            InputProps={{ inputComponent: DocumentInputMasked }}
                        />
                        <TextField
                            label="Nome do Contratante"
                            {...getFieldProps('contractorName')}
                            fullWidth
                            error={!!errors.contractorName}
                            helperText={errors.contractorName}
                        />
                        <TextField
                            label="E-mail"
                            {...getFieldProps('contractorEmail')}
                            fullWidth
                            error={!!errors.contractorEmail}
                            helperText={errors.contractorEmail}
                        />
                        <TextField
                            label="Cargo"
                            {...getFieldProps('contractorPosition')}
                            fullWidth
                            error={!!errors.contractorPosition}
                            helperText={errors.contractorPosition}
                        />
                        <TextField
                            label="Telefone"
                            {...getFieldProps('contractorPhone')}
                            fullWidth
                            error={!!errors.contractorPhone}
                            helperText={errors.contractorPhone}
                            InputProps={{ inputComponent: PhoneInputMasked }}
                        />

                        {planName && (
                            <>
                                <Divider>Plano e mensalidade</Divider>
                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        label="Plano"
                                        fullWidth
                                        value={planName}
                                        InputProps={{ readOnly: true }}
                                    />
                                    <TextField
                                        label="Mensalidade"
                                        fullWidth
                                        value={planValueLabel ?? planValue}
                                        InputProps={{
                                            readOnly: true,
                                            ...(planValueLabel ? {} : { inputComponent: PriceInputMasked }),
                                        }}
                                    />
                                </Stack>
                            </>
                        )}

                        <Divider />
                        <Button
                            variant="contained"
                            sx={{ alignSelf: 'flex-end' }}
                            disabled={!isValid || isSubmitting}
                            loading={isSubmitting}
                            onClick={handleSubmit}
                        >
                            Salvar dados
                        </Button>
                    </>
                )}

                {isReadOnly && (
                    <>
                        <Divider />
                        <Alert severity="success">Termo preenchido. Contrato gerado para assinatura.</Alert>
                    </>
                )}
            </Stack>
        </Paper>
    );
}
